import { DestroyRef, Directive, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { salesInsightsColor } from '../../utils/sales-insights-theme';
import { EMPTY, Observable, catchError, defer, map, merge, of, switchMap, tap } from 'rxjs';
import { SalesInsightsComponentDefinition, SalesInsightsFilterValues } from '../../models/sales-insights.model';
import { SalesInsightsDashboardState } from '../../services/sales-insights-dashboard-state.service';
import { SalesInsightsService } from '../../services/sales-insights.service';

type FetchResult<T> = { data: T | null; error: string | null };
type FetchRequest = { filters: SalesInsightsFilterValues; quiet: boolean };

@Directive()
export abstract class SalesInsightsBaseComponent<T> implements OnInit {
  @Input() config: SalesInsightsComponentDefinition;

  protected salesInsightsService = inject(SalesInsightsService);
  protected dashboardState = inject(SalesInsightsDashboardState);
  private destroyRef = inject(DestroyRef);

  loading = true;
  refreshing = false;
  error: string | null = null;
  data: T | null = null;
  filters: SalesInsightsFilterValues = {};

  ngOnInit(): void {
    merge(
      this.dashboardState.filters$.pipe(map(filters => ({ filters, quiet: false }) as FetchRequest)),
      this.reloads().pipe(map(() => ({ filters: this.dashboardState.filters, quiet: true }) as FetchRequest))
    )
      .pipe(
        tap(({ filters, quiet }) => {
          this.loading = this.loading || !quiet;
          this.refreshing = quiet;
          this.filters = filters;
        }),
        switchMap(({ filters }) =>
          defer(() => this.fetch(filters)).pipe(
            map(data => ({ data, error: null }) as FetchResult<T>),
            catchError(err => of({ data: null, error: this.errorMessage(err) } as FetchResult<T>))
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        this.data = result.data;
        this.error = result.error;
        this.loading = false;
        this.refreshing = false;
        this.onData(result.data);
      });
  }

  get empty(): boolean {
    return !this.loading && !this.error && this.isEmpty(this.data);
  }

  get colorRgb(): string {
    return salesInsightsColor(this.options.color);
  }

  get options(): { [key: string]: any } {
    return this.config?.options || {};
  }

  get subtitle(): string {
    return this.options.subtitle || this.defaultSubtitle();
  }

  protected defaultSubtitle(): string {
    return '';
  }

  protected periodLabelKey(): string {
    const period = this.filters.period || this.config?.source?.params?.period;
    return period ? `SALES_INSIGHTS_PERIOD_${String(period).toUpperCase()}` : '';
  }

  protected fetch(filters: SalesInsightsFilterValues): Observable<T> {
    return this.salesInsightsService.getData<T>(this.config.source, filters, this.config.accepts);
  }

  protected onData(_data: T | null): void {}

  // Fetches again without replacing the content by the loading state, for writes on the page and the component's own
  // search fields.
  protected reloads(): Observable<unknown> {
    return EMPTY;
  }

  protected abstract isEmpty(data: T | null): boolean;

  private errorMessage(err: any): string {
    return err?.error?.message || 'SALES_INSIGHTS_LOAD_FAILED';
  }
}
